import Event from '../shared/Event';
import Logger from '../shared/Logger';
import { StatusCodes } from '../shared/StatusCodes';
import { IHttpClient, RequestOptions } from './httpClient';
import { HttpAbortedError } from './HttpAbortedError';
import { HttpError } from './HttpError';
import * as Api from './model';
import type { DownloadedFile } from './DownloadedFile';

export interface SendOptions {
  /** Aborting it cancels the request, also on the server, and rejects with an `HttpAbortedError`. */
  signal?: AbortSignal;
}

export interface IApiClient {
  /** A `FileRequest` resolves to the file the server answers with, any other request to the JSON it answers with. */
  send<TResponse>(request: Api.Request<TResponse>, options?: SendOptions): Promise<TResponse>;

  connectionError: Event<{}>;
}

export default class ApiClient implements IApiClient {
  public connectionError: Event<{}> = new Event<{}>();

  constructor(
    private apiUrl: string,
    private client: IHttpClient,
    private readonly requestOptionsProvider?: IHttpRequestOptionsProvider,
  ) {
    this.send = this.send.bind(this);
    this.requestOptionsProvider ??= NullHttpRequestOptionsProvider;
  }

  public async send<TResponse>(request: Api.Request<TResponse>, { signal }: SendOptions = {}): Promise<TResponse> {
    const type = request.$type;
    const url = type ? `${this.apiUrl}/?type=${type}` : this.apiUrl;

    let response: Response;

    const options = await this.requestOptionsProvider.getForRequest(request);

    try {
      response = await this.client.post(url, JSON.stringify(request), { ...options, signal });
    } catch (error) {
      // Cancelled by the caller rather than lost: there is no connection error to report.
      if (signal?.aborted) {
        throw new HttpAbortedError();
      }

      Logger.warn(`Connection error occurred while processing request ${url}. ${error}`);

      this.connectionError.raise({});
      throw new HttpError(StatusCodes.NoConnection, 'No internet connection');
    }

    try {
      // Errors are JSON for a file request too, so only a successful one is read as a file.
      if (response.ok && request instanceof Api.FileRequest) {
        return (await readFile(response)) as TResponse;
      }

      return await readJson<TResponse>(response);
    } catch (error) {
      // The signal aborts reading the body too, which would otherwise surface as a failed response.
      if (signal?.aborted) {
        throw new HttpAbortedError();
      }

      throw error;
    }
  }
}

export interface IHttpRequestOptionsProvider {
  getForRequest<TResponse>(request: Api.Request<TResponse>): Promise<RequestOptions>;
}

export const NullHttpRequestOptionsProvider: IHttpRequestOptionsProvider = {
  getForRequest: () => Promise.resolve({}),
};

function readJson<TResponse>(response: Response): Promise<TResponse> {
  return response.json().then(
    (json) => {
      if (response.ok) {
        return json;
      } else {
        return Promise.reject(new HttpError(response.status, json.message || json));
      }
    },
    (error) => {
      return Promise.reject(HttpError.fromError(response.status, error));
    },
  );
}

async function readFile(response: Response): Promise<DownloadedFile> {
  return {
    content: await response.blob(),
    fileName: getFileName(response.headers.get('Content-Disposition')),
  };
}

/** Prefers the UTF-8 `filename*` that ASP.NET Core adds next to `filename` for names outside ASCII. */
function getFileName(contentDisposition: string | null): string | null {
  if (!contentDisposition) {
    return null;
  }

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (encoded) {
    return decodeURIComponent(encoded[1]);
  }

  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plain ? plain[1] : null;
}
